import { ChatDetailsResponse } from '@choco-admin/types';
import { Button, Card, CardBody } from '@heroui/react';
import React, { useCallback } from 'react';
import { FiCheckCircle, FiXCircle, FiZap } from 'react-icons/fi';

import { useActivateChat } from '../../../../../hooks/useActivateChat';
import {
  canActivateChat,
  getRequestFulfilmentGuidance,
  hasAssignedDistributor,
} from '../../../../../utils/requestedChatLifecycle';

interface RequestedChatLifecycleProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

/**
 * Requested-chat fulfilment panel (#123). Rendered only for `requested` chats.
 *
 * Shows guidance derived from `createdContext` and an explicit "Activate chat"
 * action (`chatUpdate({ status: 'active' })`). Activation stays disabled until
 * the required entity (a distributor) is present — the distributor itself is
 * assigned from the Distributor card in the same tab. This is a status
 * transition and is deliberately independent of the Settings-tab `enabled`
 * toggle.
 */
export const RequestedChatLifecycle: React.FC<RequestedChatLifecycleProps> = ({
  chatData,
  chatId,
}) => {
  const { activateChat, isActivating } = useActivateChat();

  const isActivated = chatData.status === 'active';
  const distributorAssigned = hasAssignedDistributor(chatData);
  const canActivate = canActivateChat(chatData);
  const guidance = getRequestFulfilmentGuidance(chatData.createdContext, {
    hasDistributor: distributorAssigned,
    isActivated,
  });

  const handleActivate = useCallback(async () => {
    try {
      await activateChat({ chatId });
    } catch {
      // Error is surfaced via a notification inside the hook; the refetch on
      // success reconciles the chat out of the `requested` state.
    }
  }, [activateChat, chatId]);

  return (
    <Card
      className="border border-warning-200 bg-warning-50/40 dark:bg-warning-900/10"
      data-testid="requested-chat-lifecycle"
    >
      <CardBody className="space-y-4 p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-100 dark:bg-warning-900/30">
            <FiZap className="h-5 w-5 text-warning-600 dark:text-warning-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              Requested chat
            </h3>
            <p className="text-sm text-default-500">
              A buyer requested this chat. Fulfil the request by assigning a
              distributor and then activating the chat.
            </p>
          </div>
        </div>

        {guidance && (
          <div className="space-y-2 rounded-xl border border-default-200 bg-content1 p-4">
            <p className="text-sm text-default-600">{guidance.intro}</p>
            <ul className="space-y-1.5">
              {guidance.steps.map((step, index) => (
                <li
                  key={step.id}
                  className="flex items-start gap-2 text-sm"
                  data-testid={`fulfilment-step-${step.id}`}
                >
                  {step.done ? (
                    <FiCheckCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-success-500"
                      aria-label="Step complete"
                    />
                  ) : (
                    <FiXCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-danger-400"
                      aria-label="Step incomplete"
                    />
                  )}
                  <span
                    className={
                      step.done
                        ? 'text-default-500 line-through'
                        : 'text-foreground'
                    }
                  >
                    {index + 1}. {step.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          {!distributorAssigned && (
            <p className="text-sm text-warning-700 dark:text-warning-300">
              Assign a distributor below before you can activate this chat.
            </p>
          )}
          <Button
            color="primary"
            className="sm:ml-auto"
            startContent={
              !isActivating ? <FiZap className="h-4 w-4" /> : undefined
            }
            onPress={handleActivate}
            isDisabled={!canActivate || isActivating}
            isLoading={isActivating}
            data-testid="activate-chat-button"
          >
            Activate chat
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};
